import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, CartesianGrid, Legend, AreaChart, Area } from 'recharts';
import API from '../utils/api';
import { PageLoader, StatCard, Badge } from '../components/common/UI';
import { formatCurrency, toJalali } from '../utils/dateHelpers';
import { EMPLOYEE_STATUS } from '../utils/statusHelpers';
import { 
  FaChartLine, FaFolderOpen, FaMoneyBillWave, FaUsers, FaCreditCard, 
  FaCheckCircle, FaVideo, FaExclamationTriangle, FaHandshake, FaChalkboardTeacher,
  FaBoxes, FaTrophy, FaCalendarAlt, FaPercent, FaArrowUp, FaArrowDown 
} from 'react-icons/fa';

const COLORS = ['#3366ff', '#1a4ef5', '#6090ff', '#0f2fb3', '#818cf8', '#60a5fa', '#22c55e', '#ef4444', '#f59e0b', '#a855f7', '#0891b2', '#db2777'];

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload?.length) {
    return (
      <div className="bg-white border border-gray-200 rounded-xl p-3 text-sm shadow-xl">
        <p className="text-gray-600 mb-1 border-b border-gray-200 pb-1">{label}</p>
        {payload.map((p, i) => (
          <p key={i} style={{ color: p.color }} className="mt-1">
            {p.name}: {typeof p.value === 'number' && p.value > 1000 ? formatCurrency(p.value) : p.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

export default function Reports() {
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  
  // داده‌های اصلی
  const [projects, setProjects] = useState([]);
  const [sponsorships, setSponsorships] = useState([]);
  const [classes, setClasses] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [equipment, setEquipment] = useState([]);
  const [finance, setFinance] = useState([]);
  const [expensesTotal, setExpensesTotal] = useState(0);
  
  // آمارهای محاسبه شده
  const [stats, setStats] = useState({
    projects: { total: 0, active: 0, completed: 0, totalAmount: 0, paidAmount: 0, receivable: 0 },
    sponsorships: { total: 0, active: 0, completed: 0, totalBudget: 0, totalCost: 0, profit: 0 },
    classes: { total: 0, active: 0, completed: 0, totalStudents: 0, totalIncome: 0, capacity: 0 },
    employees: { total: 0, active: 0, totalHours: 0, totalSalary: 0 },
    equipment: { total: 0, available: 0, inUse: 0, maintenance: 0, totalValue: 0 },
    finance: { totalIncome: 0, totalExpense: 0, profit: 0 },
  });

  // داده‌های نمودار
  const [monthlyData, setMonthlyData] = useState([]);
  const [projectStatusData, setProjectStatusData] = useState([]);
  const [projectTypeData, setProjectTypeData] = useState([]);
  const [sponsorshipPlatformData, setSponsorshipPlatformData] = useState([]);
  const [classCategoryData, setClassCategoryData] = useState([]);

  useEffect(() => {
    const load = async () => {
      try {
        const [projRes, sponRes, classRes, empRes, equipRes, finRes, expRes] = await Promise.all([
          API.get('/projects'),
          API.get('/sponsorships'),
          API.get('/classes'),
          API.get('/employees'),
          API.get('/equipment'),
          API.get('/finance'),
          API.get('/expenses'),
        ]);
        
        setProjects(projRes.data);
        setSponsorships(sponRes.data.data || []);
        setClasses(classRes.data.data || []);
        setEmployees(empRes.data);
        setEquipment(equipRes.data);
        setFinance(finRes.data);
        const totalExpenses = expRes.data.total || 0;
        setExpensesTotal(totalExpenses);
        
        calculateAllStats(projRes.data, sponRes.data.data || [], classRes.data.data || [], empRes.data, equipRes.data, finRes.data, totalExpenses);
        
      } finally { setLoading(false); }
    };
    load();
  }, []);

  const calculateAllStats = (projectsData, sponsorshipsData, classesData, employeesData, equipmentData, financeData, totalExpensesFromApi) => {
    // پروژه‌ها
    const projStats = {
      total: projectsData.length,
      active: projectsData.filter(p => p.status !== 'Delivered').length,
      completed: projectsData.filter(p => p.status === 'Delivered').length,
      totalAmount: projectsData.reduce((s, p) => s + (p.totalAmount || 0), 0),
      paidAmount: projectsData.reduce((s, p) => s + (p.paidAmount || 0), 0),
      receivable: projectsData.reduce((s, p) => s + ((p.totalAmount || 0) - (p.paidAmount || 0)), 0),
    };
    
    // اسپانسرشیپ‌ها
    const sponStats = {
      total: sponsorshipsData.length,
      active: sponsorshipsData.filter(s => s.status === 'Active').length,
      completed: sponsorshipsData.filter(s => s.status === 'Completed').length,
      totalBudget: sponsorshipsData.reduce((s, sp) => s + (sp.budget || 0), 0),
      totalCost: sponsorshipsData.reduce((s, sp) => s + (sp.cost || 0), 0),
      profit: sponsorshipsData.reduce((s, sp) => s + ((sp.budget || 0) - (sp.cost || 0)), 0),
    };
    
    // کلاس‌ها
    const classStats = {
      total: classesData.length,
      active: classesData.filter(c => c.status === 'Active').length,
      completed: classesData.filter(c => c.status === 'Completed').length,
      totalStudents: classesData.reduce((s, c) => s + (c.students?.length || 0), 0),
      totalIncome: classesData.reduce((s, c) => s + ((c.price || 0) * (c.students?.length || 0)), 0),
      capacity: classesData.reduce((s, c) => s + (c.capacity || 0), 0),
    };
    
    // کارمندان
    const empStats = {
      total: employeesData.length,
      active: employeesData.filter(e => e.status === 'Active').length,
      totalHours: employeesData.reduce((s, e) => s + (e.timeLogs?.reduce((h, l) => h + l.hours, 0) || 0), 0),
      totalSalary: employeesData.reduce((s, e) => s + (e.salary || 0), 0),
    };
    
    // تجهیزات
    const equipStats = {
      total: equipmentData.length,
      available: equipmentData.filter(e => e.status === 'Available').length,
      inUse: equipmentData.filter(e => e.status === 'InUse').length,
      maintenance: equipmentData.filter(e => e.status === 'Maintenance').length,
      totalValue: equipmentData.reduce((s, e) => s + (e.purchasePrice || 0), 0),
    };

    // مالی - محاسبه مطابق همان روشی که در صفحه «مالی» استفاده می‌شود (بر مبنای پرداخت‌های واقعی)
    let projectIncome = 0, projectTeamCost = 0, projectRentalCost = 0;
    projectsData.forEach(p => {
      projectIncome += p.paidAmount || 0;
      if (p.assignedTeamWithSalary) {
        p.assignedTeamWithSalary.forEach(emp => { projectTeamCost += emp.salary || 0; });
      }
      if (p.rentalEquipment) {
        projectRentalCost += p.rentalEquipment.reduce((sum, item) => sum + (item.price || 0), 0);
      }
    });

    let sponsorshipPaid = 0, sponsorshipCost = 0;
    sponsorshipsData.forEach(sp => {
      sponsorshipPaid += sp.paidAmount || 0;
      sponsorshipCost += sp.cost || 0;
    });

    let classIncome2 = 0;
    classesData.forEach(c => {
      classIncome2 += c.students?.reduce((sum, s) => sum + (s.paidAmount || 0), 0) || 0;
    });

    const income = projectIncome + sponsorshipPaid + classIncome2;
    const totalExpenseAll = projectTeamCost + projectRentalCost + sponsorshipCost + totalExpensesFromApi;
    
    setStats({
      projects: projStats,
      sponsorships: { ...sponStats, profit: sponsorshipPaid - sponsorshipCost },
      classes: classStats,
      employees: empStats,
      equipment: equipStats,
      finance: { totalIncome: income, totalExpense: totalExpenseAll, profit: income - totalExpenseAll },
    });

    
    // نمودارهای آماده
    const statusMap = { Planned: 'برنامه‌ریزی', 'Shooting Day': 'شوتینگ', 'Shooting Done': 'شوتینگ تمام', Editing: 'ادیت', Review: 'بررسی', Delivered: 'تحویل' };
    setProjectStatusData(projectsData.map(p => ({ name: statusMap[p.status] || p.status, value: 1 })).reduce((acc, curr) => {
      const existing = acc.find(item => item.name === curr.name);
      if (existing) existing.value++;
      else acc.push(curr);
      return acc;
    }, []));
    
    const typeMap = { Wedding: 'عروسی', Party: 'پارتی', Commercial: 'تبلیغ', Music: 'موزیک', Documentary: 'مستند', Other: 'دیگر' };
    setProjectTypeData(projectsData.map(p => ({ name: typeMap[p.type] || p.type, value: 1 })).reduce((acc, curr) => {
      const existing = acc.find(item => item.name === curr.name);
      if (existing) existing.value++;
      else acc.push(curr);
      return acc;
    }, []));
    
    // پلتفرم‌های اسپانسرشیپ
    const platformMap = { Instagram: 'اینستاگرام', YouTube: 'یوتیوب', TikTok: 'تیکتاک', Facebook: 'فیسبوک', Twitter: 'توییتر', LinkedIn: 'لینکدین' };
    setSponsorshipPlatformData(sponsorshipsData.map(s => ({ name: platformMap[s.platform] || s.platform, value: 1 })).reduce((acc, curr) => {
      const existing = acc.find(item => item.name === curr.name);
      if (existing) existing.value++;
      else acc.push(curr);
      return acc;
    }, []));
    
    // دسته‌بندی کلاس‌ها
    setClassCategoryData(classesData.map(c => ({ name: c.category, value: 1 })).reduce((acc, curr) => {
      const existing = acc.find(item => item.name === curr.name);
      if (existing) existing.value++;
      else acc.push(curr);
      return acc;
    }, []));
    
    // داده‌های ماهانه
    const monthlyMap = {};
    financeData.forEach(item => {
      const date = new Date(item.date);
      const [jy, jm] = toJalali(date.getFullYear(), date.getMonth() + 1, date.getDate());
      const key = `${jy}-${jm}`;
      const monthNames = ['حمل', 'ثور', 'جوزا', 'سرطان', 'اسد', 'سنبله', 'میزان', 'عقرب', 'قوس', 'جدی', 'دلو', 'حوت'];
      const monthName = monthNames[jm - 1];
      if (!monthlyMap[key]) monthlyMap[key] = { name: `${monthName} ${jy}`, income: 0, expense: 0, _jy: jy, _jm: jm };
      if (item.type === 'Income') monthlyMap[key].income += item.amount;
      else monthlyMap[key].expense += item.amount;
    });
    const sortedMonthly = Object.values(monthlyMap).sort((a, b) => (a._jy - b._jy) || (a._jm - b._jm));
    setMonthlyData(sortedMonthly.slice(-6).map(({ _jy, _jm, ...rest }) => rest));
  };

  const tabs = [
    { key: 'overview', label: 'خلاصه کلی', icon: FaChartLine },
    { key: 'projects', label: 'پروژه‌ها', icon: FaFolderOpen },
    { key: 'sponsorships', label: 'اسپانسرشیپ', icon: FaHandshake },
    { key: 'classes', label: 'کلاس‌ها', icon: FaChalkboardTeacher },
    { key: 'employees', label: 'کارمندان', icon: FaUsers },
    { key: 'equipment', label: 'تجهیزات', icon: FaBoxes },
    { key: 'financial', label: 'مالی پیشرفته', icon: FaMoneyBillWave },
  ];

  if (loading) return <PageLoader />;

  const profitMargin = stats.finance.totalIncome > 0 ? ((stats.finance.profit / stats.finance.totalIncome) * 100).toFixed(1) : 0;
  const classOccupancy = stats.classes.capacity > 0 ? ((stats.classes.totalStudents / stats.classes.capacity) * 100).toFixed(1) : 0;

  return (
    <div className="space-y-5 fade-in">
      {/* هدر */}
      <div>
        <h1 className="text-xl font-bold text-gray-800">گزارشات پیشرفته</h1>
        <p className="text-gray-500 text-sm mt-0.5">تحلیل جامع عملکرد کلیه بخش‌های سیستم</p>
      </div>

      {/* تب‌ها */}
      <div className="flex gap-2 overflow-x-auto pb-1 flex-wrap">
        {tabs.map(tab => (
          <button key={tab.key} onClick={() => setActiveTab(tab.key)}
            className={`flex-shrink-0 px-4 py-2 rounded-xl text-sm font-medium transition-all ${activeTab === tab.key ? 'bg-blue-600 text-white' : 'bg-gray-50 text-gray-500 hover:bg-gray-100 hover:text-gray-800'}`}>
            <tab.icon className="inline ml-1 size-4" /> {tab.label}
          </button>
        ))}
      </div>

      {/* ========== تب خلاصه کلی ========== */}
      {activeTab === 'overview' && (
        <div className="space-y-5">
          {/* کارت‌های اصلی */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard icon={<FaFolderOpen className="text-blue-600 text-lg" />} label="کل پروژه‌ها" value={stats.projects.total} sub={`${stats.projects.active} فعال`} color="blue" />
            <StatCard icon={<FaHandshake className="text-purple-600 text-lg" />} label="اسپانسرشیپ" value={stats.sponsorships.total} sub={`${stats.sponsorships.active} فعال`} color="purple" />
            <StatCard icon={<FaChalkboardTeacher className="text-yellow-600 text-lg" />} label="کلاس‌ها" value={stats.classes.total} sub={`${stats.classes.totalStudents} شاگرد`} color="yellow" />
            <StatCard icon={<FaUsers className="text-green-600 text-lg" />} label="کارمندان" value={stats.employees.total} sub={`${stats.employees.active} فعال`} color="green" />
          </div>

          {/* کارت‌های مالی */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <StatCard icon={<FaMoneyBillWave className="text-green-600 text-lg" />} label="درآمد کل" value={formatCurrency(stats.finance.totalIncome)} color="green" />
            <StatCard icon={<FaCreditCard className="text-red-600 text-lg" />} label="هزینه کل" value={formatCurrency(stats.finance.totalExpense)} color="red" />
            <StatCard icon={<FaChartLine className="text-blue-600 text-lg" />} label="سود خالص" value={formatCurrency(stats.finance.profit)} color={stats.finance.profit >= 0 ? 'green' : 'red'} />
          </div>

          {/* کارت‌های پیشرفته */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div className="glass-card p-3 text-center border border-green-200">
              <FaPercent className="text-green-600 mx-auto mb-1" />
              <div className="text-2xl font-bold text-gray-800">{profitMargin}%</div>
              <div className="text-xs text-gray-500">حاشیه سود</div>
            </div>
            <div className="glass-card p-3 text-center border border-yellow-200">
              <FaUsers className="text-yellow-600 mx-auto mb-1" />
              <div className="text-2xl font-bold text-gray-800">{classOccupancy}%</div>
              <div className="text-xs text-gray-500">ظرفیت کلاس‌ها</div>
            </div>
            <div className="glass-card p-3 text-center border border-red-200">
              <FaCreditCard className="text-red-600 mx-auto mb-1" />
              <div className="text-2xl font-bold text-gray-800">{formatCurrency(stats.projects.receivable)}</div>
              <div className="text-xs text-gray-500">باقیات مشتریان</div>
            </div>
            <div className="glass-card p-3 text-center border border-purple-200">
              <FaBoxes className="text-purple-600 mx-auto mb-1" />
              <div className="text-2xl font-bold text-gray-800">{stats.equipment.total}</div>
              <div className="text-xs text-gray-500">تجهیزات ({stats.equipment.available} موجود)</div>
            </div>
          </div>

          {/* نمودار ماهانه */}
          {monthlyData.length > 0 && (
            <div className="glass-card p-5">
              <h3 className="text-gray-800 font-semibold mb-4">روند مالی ماهانه</h3>
              <ResponsiveContainer width="100%" height={250}>
                <AreaChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" />
                  <XAxis dataKey="name" tick={{ fill: '#9ca3af', fontSize: 11 }} />
                  <YAxis tick={{ fill: '#9ca3af', fontSize: 10 }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Area type="monotone" dataKey="income" name="درآمد" stackId="1" stroke="#22c55e" fill="#22c55e40" />
                  <Area type="monotone" dataKey="expense" name="مصارف" stackId="1" stroke="#ef4444" fill="#ef444440" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {/* ========== تب پروژه‌ها ========== */}
      {activeTab === 'projects' && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard label="کل پروژه‌ها" value={stats.projects.total} color="blue" />
            <StatCard label="در حال انجام" value={stats.projects.active} color="green" />
            <StatCard label="تکمیل شده" value={stats.projects.completed} color="purple" />
            <StatCard label="باقیات" value={formatCurrency(stats.projects.receivable)} color="red" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {projectStatusData.length > 0 && (
              <div className="glass-card p-5">
                <h3 className="text-gray-800 font-semibold mb-4">وضعیت پروژه‌ها</h3>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={projectStatusData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} dataKey="value" label>
                      {projectStatusData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
            {projectTypeData.length > 0 && (
              <div className="glass-card p-5">
                <h3 className="text-gray-800 font-semibold mb-4">انواع پروژه‌ها</h3>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={projectTypeData}>
                    <XAxis dataKey="name" tick={{ fill: '#9ca3af', fontSize: 11 }} />
                    <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="value" name="تعداد" fill="#3366ff" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ========== تب اسپانسرشیپ ========== */}
      {activeTab === 'sponsorships' && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard label="کل کمپین‌ها" value={stats.sponsorships.total} color="blue" />
            <StatCard label="فعال" value={stats.sponsorships.active} color="green" />
            <StatCard label="تکمیل شده" value={stats.sponsorships.completed} color="purple" />
            <StatCard label="سود خالص" value={formatCurrency(stats.sponsorships.profit)} color={stats.sponsorships.profit >= 0 ? 'green' : 'red'} />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {sponsorshipPlatformData.length > 0 && (
              <div className="glass-card p-5">
                <h3 className="text-gray-800 font-semibold mb-4">توزیع پلتفرم‌ها</h3>
                <ResponsiveContainer width="100%" height={220}>
                  <PieChart>
                    <Pie data={sponsorshipPlatformData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} dataKey="value">
                      {sponsorshipPlatformData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}
            <div className="glass-card p-5">
              <h3 className="text-gray-800 font-semibold mb-4">خلاصه مالی</h3>
              <div className="space-y-3">
                <div className="flex justify-between"><span className="text-gray-500">بودجه کل:</span><span className="text-green-600">{formatCurrency(stats.sponsorships.totalBudget)}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">هزینه تولید:</span><span className="text-red-600">{formatCurrency(stats.sponsorships.totalCost)}</span></div>
                <div className="flex justify-between pt-2 border-t"><span className="text-gray-800 font-bold">سود:</span><span className={stats.sponsorships.profit >= 0 ? 'text-green-600 font-bold' : 'text-red-600 font-bold'}>{formatCurrency(stats.sponsorships.profit)}</span></div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========== تب کلاس‌ها ========== */}
      {activeTab === 'classes' && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard label="کل دوره‌ها" value={stats.classes.total} color="blue" />
            <StatCard label="دوره‌های فعال" value={stats.classes.active} color="green" />
            <StatCard label="کل شاگردان" value={stats.classes.totalStudents} color="purple" />
            <StatCard label="درآمد کل" value={formatCurrency(stats.classes.totalIncome)} color="green" />
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {classCategoryData.length > 0 && (
              <div className="glass-card p-5">
                <h3 className="text-gray-800 font-semibold mb-4">دسته‌بندی کلاس‌ها</h3>
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={classCategoryData}>
                    <XAxis dataKey="name" tick={{ fill: '#9ca3af', fontSize: 11 }} />
                    <YAxis tick={{ fill: '#9ca3af', fontSize: 11 }} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="value" name="تعداد" fill="#f59e0b" radius={[6, 6, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
            <div className="glass-card p-5">
              <h3 className="text-gray-800 font-semibold mb-4">آمار ظرفیت</h3>
              <div className="space-y-3">
                <div className="flex justify-between"><span className="text-gray-500">ظرفیت کل:</span><span className="text-gray-800">{stats.classes.capacity}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">شاگردان ثبت‌نام:</span><span className="text-green-600">{stats.classes.totalStudents}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">جاهای خالی:</span><span className="text-yellow-600">{stats.classes.capacity - stats.classes.totalStudents}</span></div>
                <div className="w-full bg-gray-100 rounded-full h-2 mt-2"><div className="bg-green-500 h-2 rounded-full" style={{ width: `${classOccupancy}%` }} /></div>
                <div className="text-center text-xs text-gray-500 mt-1">{classOccupancy}% ظرفیت پر شده</div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ========== تب کارمندان ========== */}
      {activeTab === 'employees' && (
        <div className="glass-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="premium-table">
              <thead><tr><th>نام</th><th>نقش</th><th>ساعات کار</th><th>معاش</th><th>وضعیت</th></tr></thead>
              <tbody>
                {employees.map(e => {
                  const hours = e.timeLogs?.reduce((s, l) => s + l.hours, 0) || 0;
                  return (
                    <tr key={e._id}>
                      <td className="font-medium text-gray-800">{e.name}</td>
                      <td className="text-gray-500">{e.role}</td>
                      <td className="text-gray-800">{hours} ساعت</td>
                      <td className="text-green-600">{formatCurrency(e.salary)}</td>
                      <td><Badge statusConfig={EMPLOYEE_STATUS[e.status]} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ========== تب تجهیزات ========== */}
      {activeTab === 'equipment' && (
        <div className="space-y-5">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard label="کل تجهیزات" value={stats.equipment.total} color="blue" />
            <StatCard label="موجود" value={stats.equipment.available} color="green" />
            <StatCard label="در استفاده" value={stats.equipment.inUse} color="yellow" />
            <StatCard label="تعمیر" value={stats.equipment.maintenance} color="red" />
          </div>
          <div className="glass-card p-5 text-center">
            <div className="text-2xl font-bold text-gray-800">{formatCurrency(stats.equipment.totalValue)}</div>
            <div className="text-gray-500 text-sm">ارزش کل تجهیزات</div>
          </div>
        </div>
      )}

      {/* ========== تب مالی پیشرفته ========== */}
      {activeTab === 'financial' && (
        <div className="space-y-5">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <StatCard icon={<FaMoneyBillWave className="text-green-600 text-lg" />} label="درآمد کل" value={formatCurrency(stats.finance.totalIncome)} color="green" />
            <StatCard icon={<FaCreditCard className="text-red-600 text-lg" />} label="هزینه کل" value={formatCurrency(stats.finance.totalExpense)} color="red" />
            <StatCard icon={<FaChartLine className="text-blue-600 text-lg" />} label="سود خالص" value={formatCurrency(stats.finance.profit)} color={stats.finance.profit >= 0 ? 'green' : 'red'} />
          </div>
          {monthlyData.length > 0 && (
            <div className="glass-card p-5">
              <h3 className="text-gray-800 font-semibold mb-4">نمودار روند مالی</h3>
              <ResponsiveContainer width="100%" height={280}>
                <LineChart data={monthlyData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(0,0,0,0.08)" />
                  <XAxis dataKey="name" tick={{ fill: '#9ca3af', fontSize: 11 }} />
                  <YAxis tick={{ fill: '#9ca3af', fontSize: 10 }} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Line type="monotone" dataKey="income" name="درآمد" stroke="#22c55e" strokeWidth={2} />
                  <Line type="monotone" dataKey="expense" name="مصارف" stroke="#ef4444" strokeWidth={2} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}
    </div>
  );
}